import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { firstValueFrom, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { AuthCompany, AuthUser } from 'src/models';

export interface CelloInstance {
  boot: (config: CelloBootConfig) => Promise<void>;
  open: (destination?: 'rewards' | 'edit-payments') => void;
  hide: () => void;
  getLabels: () => Promise<{ customLauncher: string }>;
}

export interface CelloBootConfig {
  productId: string;
  token: string;
  hideDefaultLauncher?: boolean;
  productUserDetails?: {
    email: string;
    firstName?: string;
    lastName?: string;
  };
  themeMode?: 'light' | 'dark';
}

export interface CelloTokenResponse {
  data: Array<{
    jwt: string;
  }>;
  statusCode: number;
  message: string;
}

@Injectable({ providedIn: 'root' })
export class CelloService {
  private readonly productId: string;
  private readonly integrationsApiUrl: string;
  private initialized = false;

  constructor(private readonly http: HttpClient) {
    const env = environment.env as { TD_APP_CELLO_PRODUCT_ID?: string };
    this.productId = env.TD_APP_CELLO_PRODUCT_ID || '';
    this.integrationsApiUrl = environment.integrationsApiUrl;
  }

  /**
   * Generate JWT token for Cello authentication
   * This calls your backend API endpoint that generates the token
   */
  generateToken(user: AuthUser, company: AuthCompany): Observable<string> {
    if (!this.integrationsApiUrl) {
      throw new Error('Integrations API URL not configured');
    }

    return this.http.post<CelloTokenResponse>(`${this.integrationsApiUrl}/v1/cello/token`, {
      tdUserId: user.id,
      signupDate: company.companyCreatedAt,
    }).pipe(
      map(response => {
        if (!response.data?.[0]?.jwt) {
          throw new Error('Invalid token response format');
        }
        return response.data[0].jwt;
      }),
    );
  }

  /**
   * Initialize Cello SDK with custom launcher
   */
  async initialize(user: AuthUser, company: AuthCompany): Promise<void> {
    if (this.initialized) {
      return;
    }

    if (!this.productId) {
      return;
    }

    if (!globalThis.cello) {
      return;
    }

    try {
      // Generate JWT token
      const token = await firstValueFrom(this.generateToken(user, company));
      if (!token) {
        throw new Error('Failed to generate Cello token');
      }

      // Parse user name for firstName/lastName
      const nameParts = user.name?.split(' ') || [];
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';

      // Initialize Cello using the command queue pattern
      // Wrap in Promise to ensure initialization completes before resolve
      await new Promise<void>((resolve, reject) => {
        globalThis.cello.cmd.push(async (cello: CelloInstance) => {
          try {
            await cello.boot({
              productId: this.productId,
              token,
              hideDefaultLauncher: true, // Hide default floating button
              productUserDetails: {
                email: user.email,
                firstName,
                lastName,
              },
              themeMode: 'light', // Match your app theme
            });
            this.initialized = true;
            resolve();
          } catch (error) {
            reject(error);
          }
        });
      });
    } catch (error) {
      throw error;
    }
  }

  /**
   * Show Cello referral component
   */
  show(): void {
    if (globalThis.cello && this.initialized) {
      globalThis.cello.cmd.push((cello: CelloInstance) => {
        cello.open();
      });
    }
  }

  /**
   * Get Cello labels (custom launcher text)
   * Returns a Promise that resolves with the labels object
   */
  getLabels(): Promise<{ customLauncher: string }> {
    return new Promise((resolve, reject) => {
      if (!globalThis.cello || !this.initialized) {
        reject(new Error('Cello is not initialized'));
        return;
      }

      globalThis.cello.cmd.push(async (cello: CelloInstance) => {
        try {
          const labels = await cello.getLabels();
          resolve(labels);
        } catch (error) {
          reject(error);
        }
      });
    });
  }


  /**
   * Hide Cello referral component
   */
  hide(): void {
    if (globalThis.cello && this.initialized) {
      globalThis.cello.cmd.push((cello: CelloInstance) => {
        cello.hide();
      });
    }
  }

  /**
   * Check if Cello is initialized
   */
  isInitialized(): boolean {
    return this.initialized;
  }
}
